import { SidebarProvider } from "@/components/ui/sidebar";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { ScrollArea } from "@/components/ui/scroll-area";
import { User, UserPlus, Shield, Pencil } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { ContactModal } from "@/components/crm/ContactModal";
import { ContactUserBridge } from "@/components/crm/ContactUserBridge";
import { ContactEditModal } from "@/components/crm/ContactEditModal";
import { useOrganizationContacts } from "@/hooks/use-organization-contacts";
import { useContactInvitationStatus } from "@/hooks/use-contact-invitation-status";
import { useState, useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useNavigate, useLocation, Link } from "react-router-dom";
import { toast } from "sonner";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { Checkbox } from "@/components/ui/checkbox";
import { AppSidebar } from "@/components/AppSidebar";
import { FixedPageHeader } from "@/components/ui/fixed-page-header";
import { PAGE_HEADERS } from "@/constants/page-headers";
import { useOrganizationUserRoles } from "@/hooks/use-organization-user-roles";
import { UserManagementActions } from "@/components/users/UserManagementActions";
import { Input } from "@/components/ui/input";
const OrganizationContactsList = () => {
  const queryClient = useQueryClient();
  const {
    data: contacts,
    isLoading
  } = useOrganizationContacts();
  const [editingContact, setEditingContact] = useState<any | null>(null);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);

  const handleEditClick = (contact: any) => {
    setEditingContact(contact);
    setIsEditModalOpen(true);
  };

  const handleEditSuccess = () => {
    queryClient.invalidateQueries({ queryKey: ["organization-contacts"] });
    setIsEditModalOpen(false);
    setEditingContact(null);
  };

  const handleEditClose = () => {
    setIsEditModalOpen(false);
    setEditingContact(null);
  };

  if (isLoading) {
    return <div className="text-center py-4">
        <p className="text-muted-foreground">Loading contacts...</p>
      </div>;
  }
  if (!contacts || contacts.length === 0) {
    return <div className="text-center py-4">
        <p className="text-muted-foreground">Only Contacts that are not users are shown here. </p>
      </div>;
  }
  return (
    <>
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {contacts.map(contact => (
          <ContactListItem 
            key={contact.id} 
            contact={contact}
            onEdit={handleEditClick}
          />
        ))}
      </div>
      {editingContact && (
        <ContactEditModal
          contact={editingContact}
          isOpen={isEditModalOpen}
          onClose={handleEditClose}
          onSuccess={handleEditSuccess}
        />
      )}
    </>
  );
};
const ContactListItem = ({
  contact,
  onEdit
}: {
  contact: any;
  onEdit: (contact: any) => void;
}) => {
  const {
    data: invitationStatus
  } = useContactInvitationStatus(contact.email, contact.organization_id);
  return (
    <div className="rounded-lg border bg-card p-4 flex flex-col items-center text-center gap-3">
      {contact.profile_image ? (
        <img 
          src={contact.profile_image} 
          alt={`${contact.first_name} ${contact.last_name}`} 
          className="w-16 h-16 rounded-full object-cover" 
        />
      ) : (
        <div className="w-16 h-16 rounded-full bg-muted flex items-center justify-center">
          <User className="w-8 h-8 text-muted-foreground" />
        </div>
      )}
      
      <div className="w-full">
        <p className="font-medium text-sm truncate">
          {contact.first_name} {contact.last_name}
        </p>
        <p className="text-xs text-muted-foreground truncate">{contact.email}</p>
        {contact.job_title && <p className="text-xs text-muted-foreground truncate mt-1">{contact.job_title}</p>}
      </div>
      
      <div className="w-full space-y-2">
        <Button
          variant="outline"
          size="sm"
          onClick={() => onEdit(contact)}
          className="w-full"
        >
          <Pencil className="w-4 h-4 mr-2" />
          Edit Details
        </Button>
        <ContactUserBridge contact={contact} hasPendingInvitation={invitationStatus?.hasPendingInvitation || false} />
      </div>
    </div>
  );
};
const Admin = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const queryClient = useQueryClient();
  const [organizationName, setOrganizationName] = useState("");
  const [inviteEmail, setInviteEmail] = useState("");
  const [selectedDevices, setSelectedDevices] = useState<string[]>([]);
  const [existingInvitation, setExistingInvitation] = useState<any>(null);
  const {
    data: userRole,
    isLoading: isLoadingRole
  } = useQuery({
    queryKey: ["user-role"],
    queryFn: async () => {
      const {
        data: {
          session
        }
      } = await supabase.auth.getSession();
      if (!session) {
        navigate("/");
        return null;
      }
      try {
        const {
          data: userRoles,
          error
        } = await supabase.from('user_roles').select('role').eq('user_id', session.user.id).maybeSingle();
        if (error) throw error;
        return userRoles?.role || null;
      } catch (error) {
        console.error("Error fetching user role:", error);
        toast.error("Failed to verify admin status");
        return null;
      }
    }
  });
  const {
    data: organization,
    isLoading: isLoadingOrg
  } = useQuery({
    queryKey: ["organization"],
    queryFn: async () => {
      console.log("Fetching organization data...");
      const {
        data: {
          session
        }
      } = await supabase.auth.getSession();
      if (!session) return null;
      try {
        const {
          data: userRoles,
          error: rolesError
        } = await supabase.from('user_roles').select('organization_id').eq('user_id', session.user.id).maybeSingle();
        if (rolesError) throw rolesError;
        if (!userRoles?.organization_id) {
          console.log("No organization found for user");
          return null;
        }
        const {
          data: org,
          error: orgError
        } = await supabase.from('organizations').select('*').eq('id', userRoles.organization_id).maybeSingle();
        if (orgError) throw orgError;
        console.log("Organization data fetched:", org);
        return org;
      } catch (error) {
        console.error("Error fetching organization:", error);
        toast.error("Failed to load organization data");
        return null;
      }
    },
    enabled: !!userRole
  });
  const {
    data: organizationUsers,
    isLoading: isLoadingUsers
  } = useQuery({
    queryKey: ["organization-users", organization?.id],
    queryFn: async () => {
      if (!organization?.id) {
        console.log("No organization ID found, skipping user fetch");
        return [];
      }
      try {
        console.log("Fetching organization users for org ID:", organization.id);

        // First, get user roles
        const {
          data: userRoles,
          error: rolesError
        } = await supabase.from('user_roles').select('user_id, role').eq('organization_id', organization.id);
        if (rolesError) throw rolesError;
        console.log("Raw user roles data:", userRoles);
        if (!userRoles || userRoles.length === 0) {
          console.log("No user roles found for this organization");
          return [];
        }

        // Then, fetch profiles for each user separately
        const usersWithProfiles = await Promise.all(userRoles.map(async userRole => {
          try {
            // First try to find profile by user_id
            const {
              data: profile,
              error: profileError
            } = await supabase.from('profiles').select('id, username, first_name, last_name, avatar_url, email').eq('user_id', userRole.user_id).maybeSingle();
            if (profileError) {
              console.error("Error fetching profile for user_id:", userRole.user_id, profileError);
            }
            return {
              ...userRole,
              profiles: {
                id: profile?.id || null,
                first_name: profile?.first_name || '',
                last_name: profile?.last_name || '',
                username: profile?.username || 'Unknown',
                avatar_url: profile?.avatar_url || null,
                email: profile?.email || 'No email'
              }
            };
          } catch (error) {
            console.error("Error processing user:", userRole.user_id, error);
            return {
              ...userRole,
              profiles: {
                id: null,
                first_name: '',
                last_name: '',
                username: 'Error loading',
                avatar_url: null,
                email: 'Error'
              }
            };
          }
        }));
        console.log("Users with profiles:", usersWithProfiles);
        return usersWithProfiles;
      } catch (error) {
        console.error("Error fetching organization users:", error);
        toast.error("Failed to load organization users");
        return [];
      }
    },
    enabled: !!organization?.id
  });
  const {
    data: organizationDevices,
    isLoading: isLoadingOrgDevices
  } = useQuery({
    queryKey: ["organization-devices", organization?.id],
    queryFn: async () => {
      if (!organization?.id) return [];
      try {
        // First, get all users in the organization
        const {
          data: userRoles,
          error: rolesError
        } = await supabase.from('user_roles').select('user_id').eq('organization_id', organization.id);
        if (rolesError) throw rolesError;
        if (!userRoles || userRoles.length === 0) {
          return [];
        }
        const orgUserIds = userRoles.map(ur => ur.user_id);

        // Get all device assignments for users in this organization
        const {
          data: assignments,
          error: assignmentsError
        } = await supabase.from('device_assignments').select('device_id').in('user_id', orgUserIds);
        if (assignmentsError) throw assignmentsError;
        if (!assignments || assignments.length === 0) {
          return [];
        }
        const deviceIds = [...new Set(assignments.map(a => a.device_id))]; // Remove duplicates

        // Get device details for assigned devices
        const {
          data: devices,
          error: devicesError
        } = await supabase.from('device_details').select('*').in('device_id', deviceIds);
        if (devicesError) throw devicesError;
        return devices || [];
      } catch (error) {
        console.error("Error fetching organization devices:", error);
        toast.error("Failed to load organization devices");
        return [];
      }
    },
    enabled: !!organization?.id
  });
  const {
    data: userDeviceAssignments,
    isLoading: isLoadingAssignments
  } = useQuery({
    queryKey: ["user-device-assignments"],
    queryFn: async () => {
      try {
        // Paginate to bypass the default 1000-row PostgREST cap.
        const pageSize = 1000;
        let from = 0;
        const all: { device_id: string; user_id: string }[] = [];
        // Safety cap to avoid runaway loops.
        for (let i = 0; i < 50; i++) {
          const { data, error } = await supabase
            .from('device_assignments')
            .select('device_id, user_id')
            .range(from, from + pageSize - 1);
          if (error) throw error;
          if (!data || data.length === 0) break;
          all.push(...data);
          if (data.length < pageSize) break;
          from += pageSize;
        }
        return all;
      } catch (error) {
        console.error("Error fetching device assignments:", error);
        toast.error("Failed to load device assignments");
        return [];
      }
    }
  });
  useEffect(() => {
    if (organization?.name) {
      setOrganizationName(organization.name);
    }
  }, [organization]);
  useEffect(() => {
    if (userRole && userRole !== 'admin' && userRole !== 'superuser') {
      navigate('/dashboard');
    }
  }, [userRole, navigate]);
  const updateOrganization = async () => {
    try {
      const {
        data: {
          session
        }
      } = await supabase.auth.getSession();
      if (!session) {
        toast.error("You must be logged in");
        return;
      }
      if (!organizationName.trim()) {
        toast.error("Organization name cannot be empty");
        return;
      }

      // Check if the user has any organizations in user_roles
      const {
        data: userOrgs,
        error: userOrgsError
      } = await supabase.from('user_roles').select('organization_id').eq('user_id', session.user.id).not('organization_id', 'is', null);
      if (userOrgsError) throw userOrgsError;

      // If the user has existing organizations, update the first one
      if (userOrgs && userOrgs.length > 0) {
        const firstOrgId = userOrgs[0].organization_id;
        const {
          error: updateError
        } = await supabase.from('organizations').update({
          name: organizationName
        }).eq('id', firstOrgId);
        if (updateError) throw updateError;
        toast.success("Organization updated successfully");
      } else {
        // Create new organization only if user has no organizations
        const {
          data: newOrg,
          error: createError
        } = await supabase.from('organizations').insert([{
          name: organizationName
        }]).select().single();
        if (createError) throw createError;
        const {
          error: roleError
        } = await supabase.from('user_roles').insert([{
          user_id: session.user.id,
          organization_id: newOrg.id,
          role: 'admin'
        }]);
        if (roleError) throw roleError;
        toast.success("Organization created successfully");
      }
      queryClient.invalidateQueries({
        queryKey: ["organization"]
      });
    } catch (error) {
      console.error("Error in updateOrganization:", error);
      toast.error("An unexpected error occurred");
    }
  };
  const inviteUser = async (force: boolean = false) => {
    if (!organization || !inviteEmail) {
      toast.error("Please provide an email address");
      return;
    }
    try {
      const {
        data: {
          session
        }
      } = await supabase.auth.getSession();
      if (!force) {
        const {
          data: existingInvitation,
          error: checkError
        } = await supabase.from('user_invitations').select('id, status').eq('email', inviteEmail).eq('organization_id', organization.id).maybeSingle();
        if (checkError) throw checkError;
        if (existingInvitation) {
          setExistingInvitation(existingInvitation);
          if (existingInvitation.status === 'pending') {
            toast.error("This email has already been invited and the invitation is pending", {
              action: {
                label: "Apply Anyway",
                onClick: () => inviteUser(true)
              }
            });
            return;
          } else if (existingInvitation.status === 'accepted' && !force) {
            toast.error("This email has already been invited and the invitation was accepted", {
              action: {
                label: "Apply Anyway",
                onClick: () => inviteUser(true)
              }
            });
            return;
          }
        }
      }
      const {
        data: invitationRow,
        error: upsertError
      } = await supabase.from('user_invitations').upsert([{
        organization_id: organization.id,
        email: inviteEmail,
        device_ids: selectedDevices,
        invited_by: session?.user.id,
        status: 'pending'
      }], {
        onConflict: 'email,organization_id'
      }).select().single();
      if (upsertError || !invitationRow) throw upsertError || new Error('Invitation upsert failed');
      console.log("✅ Invitation created:", {
        id: invitationRow.id,
        email: inviteEmail,
        status: invitationRow.status,
        expires_at: invitationRow.expires_at,
        organization_id: organization.id
      });

      // NEW: Check if this email already has a profile/contact
      const {
        data: existingContact
      } = await supabase.from('profiles').select('id').eq('email', inviteEmail).maybeSingle();
      console.log("🔍 Checking if contact exists for email:", inviteEmail, "Result:", existingContact);

      // Generate invite link using URL template
      const {
        data: urlTemplateData
      } = await supabase.from('url_templates').select('url_template').eq('template_type', 'user_invitation').eq('is_active', true).maybeSingle();
      let inviteLink: string;
      if (urlTemplateData?.url_template) {
        // Use custom URL template ONLY for existing contacts
        if (existingContact) {
          const templateVariables = {
            baseUrl: window.location.origin,
            invitationToken: invitationRow.id,
            contactId: existingContact.id,
            organizationId: organization.id,
            invitedRole: 'standard'
          };
          inviteLink = Object.entries(templateVariables).reduce((result, [key, value]) => {
            const placeholder = `{{${key}}}`;
            return result.replace(new RegExp(placeholder, 'g'), String(value || ''));
          }, urlTemplateData.url_template);
          console.log("📧 Generated link for EXISTING contact using custom template");
        } else {
          // For NEW users, use direct signup link (will still use custom template on signup page)
          inviteLink = `${window.location.origin}/signup?token=${invitationRow.id}&email=${encodeURIComponent(inviteEmail)}`;
          console.log("📧 Generated link for NEW user to signup page");
        }
      } else {
        // UPDATED: Choose page based on whether contact exists
        if (existingContact) {
          // Existing contact → use branded invitation page
          inviteLink = `${window.location.origin}/invite-contact?token=${invitationRow.id}&contact_id=${existingContact.id}`;
          console.log("📧 Generated link for EXISTING contact");
        } else {
          // New user → use signup page
          inviteLink = `${window.location.origin}/signup?token=${invitationRow.id}&email=${encodeURIComponent(inviteEmail)}`;
          console.log("📧 Generated link for NEW user");
        }
      }
      console.log("📧 Generated invite link:", inviteLink);
      const {
        error: emailError
      } = await supabase.functions.invoke('send-invitation', {
        body: {
          email: inviteEmail,
          organizationName: organization.name,
          inviteLink: inviteLink,
          // Fallback URL
          // Template variables for URL generation
          contactId: '',
          invitationToken: invitationRow.id,
          organizationId: organization.id,
          invitedRole: 'standard',
          baseUrl: window.location.origin
        }
      });
      if (emailError) throw emailError;
      setInviteEmail("");
      setSelectedDevices([]);
      setExistingInvitation(null);
      toast.success("Invitation sent successfully");
    } catch (error: any) {
      console.error("Error inviting user:", error);
      toast.error("Failed to send invitation");
    }
  };
  const handleLogout = async () => {
    try {
      // CRITICAL: Clear ALL cached data before signing out
      // This prevents cross-user data leakage when a new user logs in
      queryClient.clear();
      
      await supabase.auth.signOut();
      navigate("/", { state: { justLoggedOut: true } });
      toast.success("Logged out successfully");
    } catch (error) {
      toast.error("Error logging out");
      console.error("Error:", error);
    }
  };
  const handleDeviceToggle = async (userId: string, deviceId: string, isAssigned: boolean) => {
    try {
      if (isAssigned) {
        const {
          error
        } = await supabase.from('device_assignments').delete().eq('user_id', userId).eq('device_id', deviceId);
        if (error) throw error;
        toast.success("Device access removed");
      } else {
        const {
          error
        } = await supabase.from('device_assignments').insert([{
          user_id: userId,
          device_id: deviceId
        }]);
        if (error) {
          // Unique-violation: row already exists (cache was stale). Treat as success.
          if ((error as any).code === '23505') {
            toast.success("Device access already granted");
          } else {
            throw error;
          }
        } else {
          toast.success("Device access granted");
        }
      }
      queryClient.invalidateQueries({
        queryKey: ["user-device-assignments"]
      });
    } catch (error) {
      console.error("Error toggling device assignment:", error);
      toast.error("Failed to update device access");
    }
  };
  const isDeviceAssigned = (userId: string, deviceId: string) => {
    return userDeviceAssignments?.some(assignment => assignment.user_id === userId && assignment.device_id === deviceId) || false;
  };

  const { updateUserRole } = useOrganizationUserRoles();
  const [userSearch, setUserSearch] = useState("");
  const [currentUserId, setCurrentUserId] = useState<string | null>(null);
  useEffect(() => {
    supabase.auth.getSession().then(({ data }) => setCurrentUserId(data.session?.user.id ?? null));
  }, []);
  const displayName = (u: any) => {
    const full = `${u.profiles?.first_name || ''} ${u.profiles?.last_name || ''}`.trim();
    return full || u.profiles?.username || 'Anonymous User';
  };
  const term = userSearch.trim().toLowerCase();
  const matchedUsers = (organizationUsers || []).filter((u: any) => !term || displayName(u).toLowerCase().includes(term) || (u.profiles?.email || '').toLowerCase().includes(term));
  const activeUsers = matchedUsers.filter((u: any) => u.role !== 'archived');
  const archivedUsers = matchedUsers.filter((u: any) => u.role === 'archived');

  const handleRoleChange = async (userId: string, newRole: string) => {
    // Get current user's ID for validation
    const { data: { session } } = await supabase.auth.getSession();
    
    // Prevent self-demotion
    if (session?.user.id === userId && (newRole === 'standard' || newRole === 'editor')) {
      toast.error("You cannot demote yourself from administrator");
      return;
    }

    // Find the user being changed
    const user = organizationUsers?.find(u => u.user_id === userId);
    
    // Prevent changing system roles
    if (user?.role === 'superuser' || user?.role === 'partner') {
      toast.error("System roles cannot be changed from this interface");
      return;
    }

    updateUserRole.mutate({ 
      userId, 
      newRole: newRole as 'standard' | 'admin' | 'editor' 
    });
  };
  if (isLoadingRole || !userRole) {
    return null;
  }
  return <SidebarProvider>
      <div className="flex min-h-screen w-full bg-background">
        <AppSidebar />
        <main className="flex-1">
          <FixedPageHeader {...PAGE_HEADERS.admin} />
          <div className="p-8 space-y-8" style={{
          paddingTop: "8.5rem"
        }}>
            <div className="flex items-center justify-between">
            </div>

            {isLoadingOrg ? <div className="flex items-center justify-center p-8">
                <p className="text-muted-foreground">Loading organization details...</p>
              </div> : null}


            <Card className="p-6">
              <h2 className="text-lg font-semibold mb-4">Manage Users</h2>
              {isLoadingUsers || isLoadingOrgDevices || isLoadingAssignments ? <div className="text-center py-4">
                  <p className="text-muted-foreground">Loading...</p>
                </div> : organizationUsers && organizationUsers.length > 0 ? <div className="space-y-4">
                  <Input placeholder="Search by name or email" value={userSearch} onChange={e => setUserSearch(e.target.value)} className="max-w-sm" />
                  {[{ key: 'active', list: activeUsers }, { key: 'archived', list: archivedUsers }].map(group => group.list.length === 0 ? null : <div key={group.key} className={group.key === 'archived' ? 'opacity-70' : ''}>
                  {group.key === 'archived' && <h3 className="text-sm font-medium text-muted-foreground mb-2 mt-4">Archived users ({group.list.length})</h3>}
                  <Accordion type="single" collapsible className="space-y-2">
                  {group.list.map(user => <AccordionItem key={user.user_id} value={user.user_id}>
                      <AccordionTrigger className="hover:no-underline">
                        <div className="flex items-center space-x-3 p-3">
                          {user.profiles?.avatar_url ? <img src={user.profiles.avatar_url} alt={user.profiles?.username || 'User avatar'} className="w-8 h-8 rounded-full" /> : <div className="w-8 h-8 rounded-full bg-muted flex items-center justify-center">
                              <User className="w-4 h-4 text-muted-foreground" />
                            </div>}
                          <div className="text-left">
                            <p className="font-medium">
                              {displayName(user)}
                            </p>
                            <p className="text-xs text-muted-foreground">
                              {user.profiles?.email || 'No email'}
                            </p>
                          </div>
                          <Badge variant={user.role === 'archived' ? 'outline' : user.role === 'admin' || user.role === 'superuser' ? 'default' : 'secondary'} className="capitalize ml-2">{user.role === 'admin' ? 'Administrator' : user.role}</Badge>
                        </div>
                        <div className="ml-auto mr-2">
                          <UserManagementActions userId={user.user_id} profileId={user.profiles?.id || null} firstName={user.profiles?.first_name || ''} lastName={user.profiles?.last_name || ''} role={user.role} isSelf={user.user_id === currentUserId} />
                        </div>
                      </AccordionTrigger>
                      <AccordionContent className="p-4 space-y-4">
                        {/* Role Management Section */}
                        <div className="space-y-2 pb-4 border-b">
                          <h3 className="text-sm font-medium">User Role</h3>
                          <div className="flex items-center justify-between">
                            <div className="flex items-center gap-2">
                              <Badge variant={user.role === 'admin' || user.role === 'superuser' ? 'default' : 'secondary'}>
                                {user.role === 'admin' || user.role === 'superuser' ? (
                                  <>
                                    <Shield className="w-3 h-3 mr-1" />
                                    {user.role === 'superuser' ? 'Superuser' : 'Administrator'}
                                  </>
                                ) : user.role === 'editor' ? (
                                  <>
                                    <User className="w-3 h-3 mr-1" />
                                    Standard User (Edit Rights)
                                  </>
                                ) : (
                                  <>
                                    <User className="w-3 h-3 mr-1" />
                                    Standard User
                                  </>
                                )}
                              </Badge>
                            </div>
                            <Select
                              value={user.role}
                              onValueChange={(newRole) => handleRoleChange(user.user_id, newRole)}
                              disabled={user.role === 'superuser' || user.role === 'partner'}
                            >
                              <SelectTrigger className="w-[180px]">
                                <SelectValue />
                              </SelectTrigger>
                              <SelectContent>
                                <SelectItem value="standard">Standard User</SelectItem>
                                <SelectItem value="editor">Standard User with Edit Rights</SelectItem>
                                <SelectItem value="admin">Administrator</SelectItem>
                              </SelectContent>
                            </Select>
                          </div>
                          <p className="text-xs text-muted-foreground">
                            {user.role === 'admin' 
                              ? 'Administrators can manage users and organization settings' 
                              : user.role === 'superuser'
                              ? 'Superusers have full system access'
                              : user.role === 'editor'
                              ? 'Standard users with edit rights — same access as Standard User (additional edit permissions to be defined)'
                              : 'Standard users have basic access to assigned devices'}
                          </p>
                        </div>

                        {/* Device Access Management Section */}
                        <div className="space-y-4">
                          <h3 className="text-sm font-medium">Device Access Management</h3>
                          <div className="grid grid-cols-2 gap-3">
                            {organizationDevices?.map(device => <div key={device.device_id} className="flex items-center space-x-2">
                                <Checkbox id={`device-${device.device_id}-${user.user_id}`} checked={isDeviceAssigned(user.user_id, device.device_id)} onCheckedChange={checked => {
                          handleDeviceToggle(user.user_id, device.device_id, !!isDeviceAssigned(user.user_id, device.device_id));
                        }} />
                                <label htmlFor={`device-${device.device_id}-${user.user_id}`} className="text-sm leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70">
                                  {device.device_name || device.device_id}
                                  {device.device_description && <span className="text-xs text-muted-foreground ml-2">
                                      ({device.device_description})
                                    </span>}
                                </label>
                              </div>)}
                          </div>
                        </div>
                      </AccordionContent>
                    </AccordionItem>)}
                  </Accordion></div>)}
                  {activeUsers.length + archivedUsers.length === 0 && <p className="text-sm text-muted-foreground">No users match your search</p>}
                </div> : <div className="text-center py-4">
                  <p className="text-muted-foreground">No users found in the organization</p>
                </div>}
            </Card>

            <Card>
              <CardHeader>
                <CardTitle>Invite Users to Your Organisation</CardTitle>
                <CardDescription>
                  To invite someone, first add them as a contact, then send them an invitation.
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
            <div className="bg-muted p-4 rounded-lg">
              <h3 className="font-semibold mb-2"> Invitation Process:</h3>
              <ol className="list-decimal list-inside space-y-2 text-sm">
                <li>Create a contact by clicking "Add New User" below</li>
                <li>After saving, you will be able to send them an invitation from the Organisation Contacts section below</li>
                <li>They'll receive an mail with a secure invitation link</li>
                <li>When they accept and onboard, their account will be automatically linked to your organisation</li>
              </ol>
            </div>
                
                <ContactModal triggerButton={<Button className="w-full" style={{
                backgroundColor: "#DFEF97",
                color: "black"
              }}>
                      <UserPlus className="mr-2 h-4 w-4" />
                      Add New User
                    </Button>} onContactCreated={() => {
                toast.success("Contact Created", {
                  description: "You can now invite this contact from the Organisation Contacts section below."
                });
              }} />
              </CardContent>
            </Card>

            <Card className="p-6">
              <h2 className="text-lg font-semibold mb-4">Organisation Contacts</h2>
              <p className="text-sm text-muted-foreground mb-4">
                Invite contacts to access the system
              </p>
              <OrganizationContactsList />
            </Card>
          </div>
        </main>
      </div>

    </SidebarProvider>;
};
export default Admin;